

import React, { Suspense, lazy, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, MapPin, Phone, Mail, Sparkles, Zap, Brain, Shield, Users, Database, Cloud, Lock, Eye, Settings, BarChart3, FileText, PenTool, Calculator, Server, Globe, Code, Target, TrendingUp, CheckCircle, Star, Search } from 'lucide-react';
import { Toaster } from "@/components/ui/toaster";
import VTechLogo from '@/components/VTechLogo';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from "@/hooks/use-toast";

// Lazy load AI tool components
const CapabilityStatementGenerator = lazy(() => import('@/components/CapabilityStatementGenerator').then(module => ({ default: module.CapabilityStatementGenerator })));
const CustomAppMigration = lazy(() => import('@/components/CustomAppMigration').then(module => ({ default: module.CustomAppMigration })));
const CaseManagementToSalesforce = lazy(() => import('@/components/CaseManagementToSalesforce').then(module => ({ default: module.CaseManagementToSalesforce })));
const ITSMToServiceNow = lazy(() => import('@/components/ITSMToServiceNow').then(module => ({ default: module.ITSMToServiceNow })));
const InfrastructureMigrationEstimator = lazy(() => import('@/components/InfrastructureMigrationEstimator').then(module => ({ default: module.InfrastructureMigrationEstimator })));
const DigitalModernizationAssistant = lazy(() => import('@/components/DigitalModernizationAssistant'));

const LoadingFallback = () => (
  <div className="flex justify-center items-center h-96">
    <div className="text-center space-y-4">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        className="mx-auto w-16 h-16 bg-gradient-to-r from-primary to-primary/80 rounded-full flex items-center justify-center"
      >
        <Loader2 className="h-8 w-8 text-primary-foreground" />
      </motion.div>
      <p className="text-lg text-muted-foreground">Loading AI Tool...</p>
    </div>
  </div>
);

interface AITool {
  id: string;
  name: string;
  description: string;
  longDescription: string;
  icon: React.ComponentType<any>;
  color: string;
  gradient: string;
  category: 'business' | 'technical' | 'analysis' | 'migration';
  features: string[];
  component: React.ComponentType<any>;
  isNew?: boolean;
  isPopular?: boolean;
}

const aiTools: AITool[] = [
  {
    id: 'capability-statement',
    name: 'Capability Statement Generator',
    description: 'Generate professional capability statements with AI assistance',
    longDescription: 'Create compelling capability statements that showcase your organization\'s strengths, experience, and qualifications for government contracts and business opportunities.',
    icon: FileText,
    color: 'from-blue-600 to-cyan-400',
    gradient: 'bg-gradient-to-br from-blue-500/40 via-cyan-400/30 to-blue-600/35',
    category: 'business',
    features: ['AI-powered content generation', 'Professional formatting', 'Customizable templates', 'Export to multiple formats'],
    component: CapabilityStatementGenerator,
    isPopular: true
  },
  {
    id: 'digital-modernization',
    name: 'Digital Modernization Assistant',
    description: 'AI-powered digital transformation guidance and planning',
    longDescription: 'Get comprehensive guidance on modernizing your digital infrastructure, processes, and technology stack with AI-driven recommendations.',
    icon: TrendingUp,
    color: 'from-purple-600 to-pink-400',
    gradient: 'bg-gradient-to-br from-purple-500/40 via-pink-400/30 to-purple-600/35',
    category: 'technical',
    features: ['Technology assessment', 'Migration roadmap', 'Cost analysis', 'Risk evaluation'],
    component: DigitalModernizationAssistant,
    isNew: true
  },
  {
    id: 'custom-app-migration',
    name: 'Custom App Migration',
    description: 'AI-powered tool for migrating custom applications to modern platforms',
    longDescription: 'Streamline the migration of legacy custom applications to modern cloud platforms with AI-driven analysis and automated migration planning.',
    icon: Database,
    color: 'from-green-600 to-emerald-400',
    gradient: 'bg-gradient-to-br from-green-500/40 via-emerald-400/30 to-green-600/35',
    category: 'migration',
    features: ['Legacy system analysis', 'Migration planning', 'Dependency mapping', 'Testing strategies'],
    component: CustomAppMigration
  },
  {
    id: 'case-management-salesforce',
    name: 'Case Management to Salesforce',
    description: 'Migrate your case management system to Salesforce with AI guidance',
    longDescription: 'Seamlessly migrate your existing case management processes to Salesforce with AI-powered mapping and optimization recommendations.',
    icon: Users,
    color: 'from-indigo-600 to-blue-400',
    gradient: 'bg-gradient-to-br from-indigo-500/40 via-blue-400/30 to-indigo-600/35',
    category: 'migration',
    features: ['Process mapping', 'Data migration planning', 'Workflow optimization', 'Integration guidance'],
    component: CaseManagementToSalesforce
  },
  {
    id: 'itsm-servicenow',
    name: 'ITSM to ServiceNow',
    description: 'Migrate your ITSM processes to ServiceNow platform',
    longDescription: 'Transform your IT Service Management processes with AI-guided migration to ServiceNow, including process optimization and automation.',
    icon: Server,
    color: 'from-orange-600 to-red-400',
    gradient: 'bg-gradient-to-br from-orange-500/40 via-red-400/30 to-orange-600/35',
    category: 'migration',
    features: ['Process analysis', 'Service catalog design', 'Automation planning', 'Change management'],
    component: ITSMToServiceNow
  },
  {
    id: 'infrastructure-migration-estimator',
    name: 'Infrastructure Migration Estimator',
    description: 'Estimate costs and timelines for infrastructure migrations',
    longDescription: 'Get accurate cost estimates and timeline projections for infrastructure migration projects with AI-powered analysis and risk assessment.',
    icon: Calculator,
    color: 'from-teal-600 to-cyan-400',
    gradient: 'bg-gradient-to-br from-teal-500/40 via-cyan-400/30 to-teal-600/35',
    category: 'analysis',
    features: ['Cost estimation', 'Timeline projection', 'Risk assessment', 'Resource planning'],
    component: InfrastructureMigrationEstimator
  }
];

const categories = [
  { id: 'all', name: 'All Tools', icon: Sparkles },
  { id: 'business', name: 'Business', icon: Target },
  { id: 'technical', name: 'Technical', icon: Code },
  { id: 'analysis', name: 'Analysis', icon: BarChart3 },
  { id: 'migration', name: 'Migration', icon: Cloud }
];

export default function TechPlaygroundPage() {
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsPageLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  const filteredTools = aiTools.filter(tool => {
    const matchesCategory = selectedCategory === 'all' || tool.category === selectedCategory;
    const matchesSearch = tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         tool.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleToolSelect = (toolId: string) => {
    setSelectedTool(toolId);
    toast({
      title: "AI Tool Selected",
      description: `Opening ${aiTools.find(t => t.id === toolId)?.name}...`,
    });
  };

  const handleCloseTool = () => {
    setSelectedTool(null);
    toast({
      title: "Returning to Dashboard",
      description: "You can select another AI tool or return to the main dashboard.",
    });
  };

  const selectedToolData = selectedTool ? aiTools.find(tool => tool.id === selectedTool) : null;

  if (isPageLoading) {
    return (
      <div className="flex flex-col min-h-screen bg-main-bg text-foreground">
        <Toaster />
        <header className="container mx-auto px-4 py-4 flex justify-between items-center">
          <VTechLogo />
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild className="flex items-center gap-2">
              <Link to="/">
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Link>
            </Button>
          </div>
        </header>
        <main className="flex-grow flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center space-y-6"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="mx-auto w-20 h-20 bg-gradient-to-r from-primary to-primary/80 rounded-full flex items-center justify-center"
            >
              <Sparkles className="h-10 w-10 text-primary-foreground" />
            </motion.div>
            <div className="space-y-2">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                Loading AI Toolbox
              </h2>
              <p className="text-muted-foreground text-lg">Preparing your advanced AI tools...</p>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-main-bg text-foreground">
      <Toaster />
      
      {/* Header with Apple liquid glass effect */}
      <motion.header 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="sticky top-0 z-50 border-b border-border/50 bg-main-bg/80 backdrop-blur-xl"
      >
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <VTechLogo />
          <div className="flex items-center gap-3">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button variant="outline" asChild className="flex items-center gap-2 btn-liquid-glass">
                <Link to="/">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Dashboard
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </motion.header>

      <main className="flex-grow bg-main-bg ">
        <AnimatePresence mode="wait">
          {!selectedTool ? (
            // AI Tools Dashboard
            <motion.div
              key="dashboard"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="container mx-auto p-6 space-y-8"
            >
              {/* Hero Section */}
              <div className="text-center space-y-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="space-y-4"
                >
                  <div className="inline-flex items-center space-x-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2">
                    <Sparkles className="h-4 w-4 via-primary/90 to-accent bg-clip-text" />
                    <span className="text-sm font-medium via-primary/90 to-accent bg-clip-text">AI-Powered Solutions</span>
                  </div>
                  <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-primary via-primary/90 to-accent bg-clip-text ">
                    AI Toolbox
                  </h1>
                  <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                    Discover cutting-edge AI solutions designed to transform your business operations, 
                    streamline migrations, and drive digital innovation.
                  </p>
                </motion.div>

                {/* Search and Filter */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="max-w-2xl mx-auto space-y-4"
                >
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search AI tools..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full px-4 py-3 pl-12 search-input-liquid text-white placeholder-white/60"
                    />
                                          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/60" />
                  </div>
                  
                  <div className="flex flex-wrap justify-center gap-1.5">
                    {categories.map((category) => (
                      <motion.button
                        key={category.id}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 whitespace-nowrap category-filter-liquid ${
                          selectedCategory === category.id
                            ? 'active text-white'
                            : 'text-white/80 hover:text-white'
                        }`}
                      >
                        <category.icon className="h-4 w-4 flex-shrink-0" />
                        <span className="text-sm font-medium">{category.name}</span>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              </div>

              {/* AI Tools Grid */}
             <motion.div
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.6, delay: 0.3 }}
  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-2 gap-y-14"
>
  {filteredTools.map((tool, index) => (
    <motion.div
      key={tool.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.1 * index }}
      whileHover={{ scale: 1.02, y: -1 }}
      whileTap={{ scale: 0.98 }}
      className="group cursor-pointer"
      onClick={() => handleToolSelect(tool.id)}
    >
      <div
        className={`p-6 rounded-2xl transition-all duration-300 hover:scale-105 hover:shadow-2xl ${tool.gradient} backdrop-blur-xl shadow-lg h-[500px] w-72 flex flex-col ai-tool-card`}
      >
        {/* Badges + Icon */}
        <div className="flex items-center justify-center mb-3 relative">
          <div className="absolute top-2 left-2 flex items-center gap-1">
            {tool.isNew && (
              <span className="px-1 py-0.5 text-xs font-semibold bg-green-400/30 text-green-200 rounded-full">
                New
              </span>
            )}
            {tool.isPopular && (
              <span className="px-1 py-0.5 text-xs font-semibold bg-orange-400/30 text-orange-200 rounded-full">
                Popular
              </span>
            )}
          </div>
          <div
            className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${tool.color} text-white shadow-lg flex items-center justify-center relative overflow-hidden`}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-2xl"></div>
            <tool.icon className="h-8 w-8 relative z-10" />
          </div>
        </div>

        {/* Content */}
        <div className="text-center space-y-4 flex-grow overflow-hidden">
          <h3 className="text-lg font-semibold text-white group-hover:text-blue-200 transition-colors leading-tight">
            {tool.name}
          </h3>
          <p className="text-white/80 text-sm leading-relaxed px-2">
            {tool.description}
          </p>

          {/* Features List */}
          <div className="space-y-2 max-h-[220px] overflow-y-auto">
            <h4 className="text-xs font-semibold text-white/90 uppercase tracking-wide">
              Key Features
            </h4>
            <ul className="space-y-1 text-left">
              {tool.features.slice(0, 4).map((feature, idx) => (
                <li key={idx} className="flex items-center text-xs text-white/70">
                  <div className="w-1.5 h-1.5 bg-white/60 rounded-full mr-2 flex-shrink-0"></div>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Action Button */}
        <div className="mt-auto pt-4">
          <div className="inline-flex items-center justify-center w-full py-2 px-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white/90 text-xs font-medium hover:bg-white/20 transition-all duration-200">
            Launch Tool
            <ArrowLeft className="h-3 w-3 ml-1 rotate-180" />
          </div>
        </div>
      </div>
    </motion.div>
  ))}
</motion.div>


              {/* Empty State */}
              {filteredTools.length === 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-12 space-y-4"
                >
                  <div className="mx-auto w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center">
                    <Search className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-semibold">No tools found</h3>
                  <p className="text-muted-foreground">Try adjusting your search or filter criteria.</p>
                </motion.div>
              )}
            </motion.div>
          ) : (
            // Tool Workspace
            <motion.div
              key="workspace"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.4 }}
              className="flex h-screen"
            >
              {/* Sidebar */}
              <div className="w-80 bg-card/20 backdrop-blur-xl border-r border-border/50 flex flex-col">
                <div className="p-6 border-b border-border/50">
                  <h3 className="font-semibold text-xl mb-2">AI Tools</h3>
                  <p className="text-sm text-muted-foreground">Available tools</p>
                </div>
                
                <div className="flex-1 p-4 space-y-2 overflow-y-auto">
                  {aiTools.map((tool) => (
                    <button
                      key={tool.id}
                      className={`w-full text-left p-4 rounded-xl transition-all duration-300 ${
                        selectedTool === tool.id 
                          ? 'bg-primary/20 text-primary border border-primary/30 shadow-lg' 
                          : 'hover:bg-card/50 border border-transparent hover:border-border/30'
                      }`}
                      onClick={() => handleToolSelect(tool.id)}
                    >
                      <div className="flex items-center">
                        <div className={`p-2 rounded-lg bg-gradient-to-r ${tool.color} text-white mr-3`}>
                          <tool.icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-sm">{tool.name}</div>
                          <div className="text-xs text-muted-foreground mt-1">{tool.description}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                
                <div className="p-4 border-t border-border/50">
                  <button 
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl hover:bg-card/70 transition-all duration-300" 
                    onClick={handleCloseTool}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Dashboard
                  </button>
                </div>
              </div>

              {/* Main Content */}
              <div className="flex-1 bg-main-bg overflow-hidden flex flex-col ">
                {selectedToolData && (
                  <div className="flex-1 overflow-auto mr-2">
                    <div className="p-8">
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className={`p-4 rounded-xl bg-gradient-to-r ${selectedToolData.color} text-white shadow-lg`}>
                              <selectedToolData.icon className="h-8 w-8" />
                            </div>
                            <div>
                              <h2 className="text-3xl font-bold">{selectedToolData.name}</h2>
                              <p className="text-muted-foreground text-lg">{selectedToolData.longDescription}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="border-t border-border/50 pt-6">
                          <Suspense fallback={<LoadingFallback />}>
                            <selectedToolData.component />
                          </Suspense>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
      
     {/* Footer */}
  <Footer />   {/* ✅ render Footer here inside Home */}

</div>
  )
}

// ✅ Define Footer as a helper component (NOT export default)
function Footer() {
  const domain = typeof window !== "undefined" ? window.location.hostname : "";

  const isCom = domain.endsWith(".com");
  const isCa = domain.endsWith(".ca");
    const isLocal = domain === "localhost" || domain === "127.0.0.1";

  return (
    <motion.footer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8, delay: 1 }}
      className="bg-card border-t border-border/50 relative z-10"
    >
      <div className="container-professional px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <VTechLogo />
            <p className="text-muted-foreground max-w-sm leading-relaxed">
              {isCom &&
                "vTech is a Managed IT Services firm based in Washington DC, specializing in Cloud Computing and Managed Network Security solutions."}
              {isCa &&
                "vTech is a leading IT Services provider in Canada, offering Cloud Solutions and Cybersecurity services tailored for Canadian enterprises."}
              {!isCom && !isCa &&
                "vTech provides IT Services across multiple regions with expertise in Cloud and Security."}
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Contact Information</h3>
            <ul className="space-y-3 text-muted-foreground">
              {isCom && (
                <>
                  <li className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-primary flex-shrink-0" />
                    <span>1100 H St NW, STE 850, Washington, DC, 20005-5479</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-primary" />
                    <span>+1 202-644-9774</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-primary" />
                    <span>info@vtechsolution.com</span>
                  </li>
                </>
              )}

              {isCa && (
                <>
                   <li className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-primary flex-shrink-0" />
                    <span>302-1200 PemBina Highway, winnipeg,MB R3T 2A7 Canada</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-primary" />
                    <span>431.801.8200</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-primary" />
                    <span>info@vtechsolution.ca</span>
                  </li>
                </>
              )}
              {isLocal && (
                <>
                  <li className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-primary flex-shrink-0" />
                    <span>302-1200 PemBina Highway, winnipeg,MB R3T 2A7 Canada</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-primary" />
                    <span>431.801.8200</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-primary" />
                    <span>info@vtechsolution.ca</span>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>

        <div className="border-t border-border/50 mt-8 pt-6 text-center text-sm text-muted-foreground">
          © {new Date().getFullYear()} vTech Solution, Inc. All rights reserved.
        </div>
      </div>
    </motion.footer>
  );
}
